// src/pages/admin/Building/Bookings.jsx
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FaEye,
  FaEdit,
  FaTrash,
  FaSearch,
  FaTimes,
  FaMoneyBillWave,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaSpinner,
  FaFilter,
  FaUser,
  FaBuilding,
} from 'react-icons/fa';
import { useDispatch } from "react-redux";
import { successToast, errorToast } from '../../Utils/toast';
import { openModal } from "../../redux/features/modalSlice";
import Pagination from '../../common/Pagination';
import SearchBar from '../../common/Search';
import { FaCalendarAlt, FaSyncAlt } from "react-icons/fa";

import {
  useGetAdminBookingsQuery,
  useUpdateBookingStatusMutation,
} from '../../redux/services/buildingService/building.Services';
import CommonBreadcrumb from '../../common/CommonBreadcrumb';
function Bookings() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loadingId, setLoadingId] = useState(null);

  // API Hooks
  const { data, isLoading, isError, refetch } = useGetAdminBookingsQuery({
    page: 1,
    limit: 20,
    status: statusFilter,
    search: searchTerm,
  });

  const [updateBookingStatus] = useUpdateBookingStatusMutation();

  const bookings = data?.data || [];
  const pagination = data?.pagination || {};
  const totalPages = pagination?.totalPages || 0;
  const totalItems = pagination?.total || 0;

  // Get status badge
  const getStatusBadge = (status) => {
    const statusMap = {
      'pending': { class: 'badge bg-warning', label: 'Pending' },
      'confirmed': { class: 'badge bg-info', label: 'Confirmed' },
      'checked_in': { class: 'badge bg-primary', label: 'Checked In' },
      'checked_out': { class: 'badge bg-success', label: 'Checked Out' },
      'cancelled': { class: 'badge bg-danger', label: 'Cancelled' },
    };
    const statusInfo = statusMap[status] || statusMap['pending'];
    return <span className={statusInfo.class}>{statusInfo.label}</span>;
  };

  // Get payment status badge
  const getPaymentStatusBadge = (status) => {
    const statusMap = {
      'pending': { class: 'badge bg-warning', label: 'Pending' },
      'partial': { class: 'badge bg-info', label: 'Partial' },
      'paid': { class: 'badge bg-success', label: 'Paid' },
    };
    const statusInfo = statusMap[status] || statusMap['pending'];
    return <span className={statusInfo.class}>{statusInfo.label}</span>;
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Format price
  const formatPrice = (price) => {
    if (!price) return 'N/A';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(price);
  };

  // Handle status change
  const handleStatusChange = async (id, status) => {
    if (!window.confirm(`Are you sure you want to change status to "${status}"?`)) {
      return;
    }

    try {
      setLoadingId(id);
      await updateBookingStatus({ id, status }).unwrap();
      successToast(`Booking status updated to ${status}`);
      refetch();
    } catch (error) {
      errorToast(error?.data?.message || 'Failed to update status');
    } finally {
      setLoadingId(null);
    }
  };

  // Handle search
  const handleSearch = (value) => {
    setSearchTerm(value);
  };

  // Clear filters
  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
  };

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <h5 className="mt-3">Loading Bookings...</h5>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-center py-5">
        <div className="alert alert-danger">
          <h5>Something Went Wrong</h5>
          <p>Unable to load bookings. Please try again.</p>
          <button className="btn btn-primary" onClick={() => refetch()}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* HEADER */}
      {/* <div className="page-header-breadcrumb all_breadcrumb">
        <div className="ms-md-1 mb-1 mb-md-0 ms-0 page_heading_breadcrumb">
          <nav>
            <h4 className="fw-semibold">
              📅 Bookings {totalItems > 0 && <span className="badge-custom bg-primary">{totalItems}</span>}
            </h4>
            <ol className="breadcrumb mb-0">
              <li className="breadcrumb-item active">
                <Link to="/admin">Home</Link>
              </li>
              <li className="breadcrumb-item">Bookings</li>
            </ol>
          </nav>
        </div>
        <div className="page-title fw-semibold fs-18 mb-0">
          <button
            className="btn btn-primary"
            onClick={() => refetch()}
          >
            🔄 Refresh
          </button>
        </div>
      </div> */}
<CommonBreadcrumb
    title="Bookings"
    count={totalItems}
    breadcrumbs={[
        { label: "Home", link: "/admin" },
        { label: "Bookings" },
    ]}
    buttons={[
        {
            text: "Refresh",
            icon: <FaSyncAlt className="me-1" />,
            onClick: refetch,
            className: "btn btn-primary",
        },
    ]}
/>
      {/* SEARCH AND FILTER SECTION */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3 align-items-center">
            <div className="col-md-4">
              <SearchBar
                search={searchTerm}
                setSearch={handleSearch}
                placeholder="Search by name, email, phone, booking ID..."
              />
            </div>

            <div className="col-md-3">
              <select
                className="form-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="checked_in">Checked In</option>
                <option value="checked_out">Checked Out</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            <div className="col-md-5 text-md-end">
              {(searchTerm || statusFilter) && (
                <button
                  className="btn btn-outline-secondary me-2"
                  onClick={clearFilters}
                >
                  <FaTimes className="me-1" />
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* TABLE */}
      <div className="st-wrapper-card">
        {bookings.length === 0 ? (
          <div className="text-center py-5">
            <div className="mb-3">
              <FaCalendarAlt className="text-muted" size={48} />
            </div>
            <h5>No Bookings Found</h5>
            <p className="text-muted">
              {searchTerm || statusFilter
                ? "Try adjusting your search or filter criteria"
                : "No bookings available"}
            </p>
          </div>
        ) : (
          <div className="st-table-responsive">
            <table className="table st-custom-table align-middle mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Booking ID</th>
                  <th>User</th>
                  <th>Building</th>
                  <th>Dates</th>
                  <th>Amount</th>
                  <th>Payment</th>
                  <th>Status</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {bookings.map((item, index) => (
                  <tr key={item.id}>
                    <td>{index + 1}</td>
                    <td>
                      <span className="fw-bold text-primary">#{item.booking_id}</span>
                    </td>
                    <td>
                      <div>
                        <div className="fw-semibold">{item.full_name}</div>
                        <small className="text-muted">{item.email}</small>
                      </div>
                    </td>
                    <td>
                      <div className="fw-semibold">{item.building_name}</div>
                      <small className="text-muted">{item.building_city}</small>
                    </td>
                    <td>
                      <div>
                        <FaCalendarAlt className="me-1" size={12} />
                        {formatDate(item.check_in_date)}
                      </div>
                      <div>
                        <FaCalendarAlt className="me-1" size={12} />
                        {formatDate(item.check_out_date)}
                      </div>
                      <small className="text-muted">{item.total_days} days</small>
                    </td>
                    <td>
                      <div className="fw-semibold">{formatPrice(item.total_amount)}</div>
                      <small className="text-muted">Paid: {formatPrice(item.paid_amount)}</small>
                    </td>
                    <td>{getPaymentStatusBadge(item.payment_status)}</td>
                    <td>
                      <select
                        className="form-select form-select-sm"
                        value={item.status}
                        onChange={(e) => handleStatusChange(item.id, e.target.value)}
                        disabled={loadingId === item.id}
                        style={{ minWidth: '120px' }}
                      >
                        <option value="pending">Pending</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="checked_in">Checked In</option>
                        <option value="checked_out">Checked Out</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </td>
                    <td className="text-center">
                      {/* VIEW */}
                      <button
                        className="st-action-btn st-view-btn me-1"
                        onClick={() =>
                          dispatch(
                            openModal({
                              type: "view-booking",
                              title: "View Booking",
                              data: item,
                            })
                          )
                        }
                        title="View Details"
                        disabled={loadingId === item.id}
                      >
                        <FaEye />
                      </button>

                      {/* USER */}
                      {item.user_id && (
                        <button
                          className="st-action-btn st-status-btn me-1"
                          onClick={() => navigate(`/admin/user-details/${item.user_id}`)}
                          title="View User"
                          disabled={loadingId === item.id}
                        >
                          <FaUser />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* PAGINATION */}
        {totalPages > 1 && (
          <div className="d-flex justify-content-end mt-4">
            <Pagination
              page={1}
              totalPages={totalPages}
              onPageChange={(page) => {
                // Handle page change
                console.log('Page changed:', page);
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default Bookings;